import { BidStatus, type AdminBid, type RelayQueueItem } from '../bids/types';
import { VerificationStatus, type User } from '../clients/types';

/**
 * Pure derivations behind the dashboard tiles. Kept out of the hook so the
 * counting rules — which are the only place this screen can be wrong — are
 * testable without a QueryClient.
 */

/** Relay rows the desk still owes the real auction (everything pre-`relayed`). */
const OPEN_RELAY_STATUSES = new Set(['captured', 'queued']);

/** Bids captured from clients but not yet mirrored on the auction. */
export function openRelayRows(rows: RelayQueueItem[]): RelayQueueItem[] {
  return rows.filter((row) => OPEN_RELAY_STATUSES.has(row.relayStatus));
}

/**
 * Open rows whose SLA badge already went red — the operator is late, and this
 * is the one number on the board that means a client may be losing a lot.
 */
export function countOverdueRelay(rows: RelayQueueItem[]): number {
  return openRelayRows(rows).filter((row) => row.slaSeverity === 'red').length;
}

/**
 * Clients who still need a verification decision. A row with no
 * `verificationStatus` counts as pending: the field is optional on User and an
 * absent value is an unverified account, never an approved one.
 */
export function countAwaitingVerification(users: User[]): number {
  return users.filter(
    (user) =>
      (user.verificationStatus ?? VerificationStatus.PENDING) !== VerificationStatus.APPROVED,
  ).length;
}

/** Bids still live on a lot (`Under Review` — see BidStatus for the wire values). */
export function countLiveBids(bids: AdminBid[]): number {
  return bids.filter((bid) => bid.status === BidStatus.Active).length;
}
